import * as React from 'react';
import { useSelector } from 'react-redux';

import { IBuilder, ISeller } from '../../types/newbuildingsCompare';
import { selectDeviceType } from '../../selectors/page/selectDeviceType';
import { isValidPhoneObject } from '../../utils/phone/isValidPhoneObject';
import { CallButton } from './CallButton';
import { ShowPhones } from './ShowPhones';

export interface INewbuildingCallToActionProps {
  newbuildingId: number;
  url: string;
  builders: IBuilder[];
  sellers: ISeller[];
  onlyIcon?: boolean;
  buttonText?: string;
  showButtonIcon?: boolean;
  fullWidth?: boolean;
}

/**
 * Кнопка звонка застройщику/продавцу для мобильной страницы сравнения.
 * Один контакт → сразу `tel:`; несколько контактов (или планшет) → модалка со списком.
 */
export const NewbuildingCallToAction: React.FC<INewbuildingCallToActionProps> = ({
  newbuildingId,
  url,
  builders,
  sellers,
  onlyIcon,
  buttonText,
  showButtonIcon,
  fullWidth,
}) => {
  const deviceType = useSelector(selectDeviceType);

  const buildersWithPhones = React.useMemo(
    () => builders.filter(builder => isValidPhoneObject({ phone: builder.phone })),
    [builders],
  );
  const sellersWithPhones = React.useMemo(
    () => sellers.filter(seller => isValidPhoneObject({ phone: seller.phone })),
    [sellers],
  );

  const target = buildersWithPhones[0] || sellersWithPhones[0];

  if (!target) {
    return null;
  }

  const showModal = buildersWithPhones.length + sellersWithPhones.length > 1 || deviceType === 'tablet';

  if (showModal) {
    return (
      <ShowPhones
        onlyIcon={onlyIcon}
        buttonText={buttonText}
        fullWidth={fullWidth}
        builders={buildersWithPhones}
        newbuildingId={newbuildingId}
        sellers={sellersWithPhones}
        showButtonIcon={showButtonIcon}
        url={url}
      />
    );
  }

  return (
    <CallButton
      onlyIcon={onlyIcon}
      buttonText={buttonText}
      fullWidth={fullWidth}
      isBuilder={buildersWithPhones.length > 0}
      newbuildingId={newbuildingId}
      target={target}
      showButtonIcon={showButtonIcon}
      url={url}
    />
  );
};
